import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, customerReqConfig } from "../../include/api";
import {
  CANCEL_REQUEST,
  CANCELLED,
  DELIVERED,
  ERROR,
  ORDERED,
  PACKED,
  SHIPPED,
  SUCCESS,
} from "../../include/constant";
import { getCustomer, preventClick, ratingAvg1 } from "../../include/function";
import { useNotification } from "../../Toast/ToastProvider";
import Error from "../add-ons/Error";
import Modal from "../add-ons/Modal";
import Spinner from "../add-ons/Spinner";
import { CheckCircle } from "react-feather";
import { CircleFadingArrowUp, CircleX } from "lucide-react";
import "../../styles/ProgressBar.css";
import ProductImage from "./ProductImage";

const CustomerOrders = ({ from }) => {
  const user = getCustomer();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [modalComponent, setModalComponent] = useState("");
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const [products, setProducts] = useState([]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 10);
  };

  const fetchProductDetails = useCallback(
    (productIds) => {
      const uniqueIds = [...new Set(productIds)];
      uniqueIds.forEach((id) => {
        if (!products[id]) {
          api
            .get(`/product/?id=${id}`, customerReqConfig())
            .then((response) => {
              if (response.status === 200 && response.data.data.length > 0) {
                setProducts((prev) => ({
                  ...prev,
                  [id]: response.data.data[0],
                }));
              }
            })
            .catch((error) => console.error("Error fetching product:", error));
        }
      });
    },
    [products],
  );

  const getOrders = useCallback(() => {
    setLoading(true);
    api
      .get("/orders/?customerId=" + user.id, customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setOrders(response.data.data);
          const productIds = response.data.data.flatMap((order) =>
            order.orderItems.map((item) => item.productId),
          );
          fetchProductDetails(productIds);
        }
      })
      .catch((error) => {
        setOrders([]);
      })
      .finally(() => setLoading(false));
  }, [user.id, fetchProductDetails]);

  useEffect(() => {
    getOrders();
  }, []);

  const handleShowCancelModal = (order) => {
    setModalComponent(
      <Cancel
        orderItemIds={order.orderItems.map((item) => item.id)}
        getOrderItems={getOrders}
        setShowModal={() => setModalComponent(null)}
      />,
    );
  };

  useEffect(() => {
    api
      .get("/rating_and_review/", customerReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setAllReviewData(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching reviews:", error);
      });
  }, []);

  const [allReviewData, setAllReviewData] = useState([]);

  const getFilteredOrders = () => {
    let result = [...orders];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result
        .map((order) => {
          const matchingItems = order.orderItems.filter((item) => {
            const nameMatch = item.productName?.toLowerCase().startsWith(term);
            const priceMatch = item.amount.toString().startsWith(searchTerm);
            return nameMatch || priceMatch;
          });

          if (matchingItems.length > 0) {
            return {
              ...order,
              orderItems: matchingItems,
            };
          }
          return null;
        })
        .filter((order) => order !== null);
    }

    result = result
      .map((order) => {
        const filteredItems = order.orderItems.filter((item) => {
          const status = item.status?.toLowerCase() || "";

          if (filter === "completed") {
            return status === "delivered";
          } else if (filter === "ongoing") {
            return !["delivered", "cancelled"].includes(status);
          }
          return true;
        });

        if (filteredItems.length > 0) {
          return {
            ...order,
            orderItems: filteredItems,
          };
        }

        return null;
      })
      .filter((order) => order !== null);

    return result;
  };

  const filteredOrders = getFilteredOrders();

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);

    const day = String(d.getDate()).padStart(2, "0");
    const month = d.toLocaleString("en-US", { month: "short" });
    const year = d.getFullYear();

    return `${day} ${month}, ${year}`;
  };

  const formatStatusDate = (order, status) => {
    const statusLower = status.toLowerCase();
    let timestamp = "";

    switch (statusLower) {
      case "ordered":
        timestamp = order.createTimestamp;
        break;
      case "packed":
        timestamp = order.packedTimestamp;
        break;
      case "shipped":
        timestamp = order.shippedTimestamp;
        break;
      case "delivered":
        timestamp = order.deliveredTimestamp;
        break;
      case "cancelled":
        timestamp = order.cancelledTimestamp;
        break;
      default:
        timestamp = order.createTimestamp;
    }

    return timestamp ? formatDate(timestamp) : "";
  };

  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const allSteps = selectedOrder
    ? [
        { label: ORDERED, date: selectedOrder.createTimestamp },
        { label: PACKED, date: selectedOrder.packedTimestamp },
        { label: SHIPPED, date: selectedOrder.shippedTimestamp },
        { label: DELIVERED, date: selectedOrder.deliveredTimestamp },
        { label: CANCELLED, date: selectedOrder.cancelledTimestamp },
      ].filter((step) => step.date || step.label === ORDERED)
    : [];

  return (
    <div className={from === "orders" ? "fixed-top-bar" : ""}>
      {loading ? (
        <div className="py-5">
          <Spinner color="danger" />
        </div>
      ) : (
        <>
          {orders?.length > 0 ? (
            <div>
              {showModal && (
                <Modal
                  visible={showModal}
                  onClose={() => setShowModal(false)}
                  size="lg"
                  centered={true}
                >
                  <div className="order-tracking-container">
                    {allSteps.map((step, index) => (
                      <div key={index} className="order-step">
                        <div
                          className={`order-circle ${step.date ? "completed" : "incomplete"}`}
                        >
                          {step.date && (
                            <i className="fas fa-check text-white" />
                          )}
                        </div>

                        {index < allSteps.length - 1 && (
                          <div
                            className={`order-line1 ${allSteps[index + 1]?.date ? "" : "incomplete"}`}
                          ></div>
                        )}

                        <div className="order-content">
                          <h6 className="fw-bold text-dark mb-1 fs-6">
                            {step.label}{" "}
                            {step.date && `, ${formatDate(step.date)}`}
                          </h6>
                          <p className="text-dark small mb-0">
                            {step.label === ORDERED &&
                              "Your order has been confirmed."}
                            {step.label === PACKED &&
                              "Your order has been processed."}
                            {step.label === SHIPPED &&
                              "Your order has been shipped."}
                            {step.label === DELIVERED &&
                              "Your order has been delivered."}
                            {step.label === CANCELLED &&
                              "Your order has been cancelled."}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Modal>
              )}
              <div className="row gy-2 px-16 my-32">
                <div className="col-xl-12 col-lg-12">
                  <div className="cart-table border border-gray-100 rounded-16 px-40 py-40">
                    <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                      <h6 className="text-xl mb-32 fw-bold ms-80 me-80">
                        My Orders
                      </h6>
                      <div className="d-flex gap-3 mb-24 ms-80 me-80">
                        <button
                          className={`btn btn-sm flex-center fw-bold rounded-16 py-16 ${filter === "all" ? "btn-main text-white" : "text-neutral-600 hover-bg-main-600 hover-text-white"}`}
                          style={{
                            width: "125px",
                            backgroundColor:
                              filter === "all" ? undefined : "#ddd",
                            border: "none",
                          }}
                          onClick={() => setFilter("all")}
                        >
                          All
                        </button>
                        <button
                          className={`btn btn-sm flex-center fw-bold rounded-16 py-16 ${filter === "ongoing" ? "btn-main text-white" : "text-neutral-600 hover-bg-main-600 hover-text-white"}`}
                          style={{
                            width: "125px",
                            backgroundColor:
                              filter === "ongoing" ? undefined : "#ddd",
                            border: "none",
                          }}
                          onClick={() => setFilter("ongoing")}
                        >
                          Ongoing
                        </button>
                        <button
                          className={`btn btn-sm flex-center fw-bold rounded-16 py-16 ${filter === "completed" ? "btn-main text-white" : "text-neutral-600 hover-bg-main-600 hover-text-white"}`}
                          style={{
                            width: "125px",
                            backgroundColor:
                              filter === "completed" ? undefined : "#ddd",
                            border: "none",
                          }}
                          onClick={() => setFilter("completed")}
                        >
                          Completed
                        </button>
                      </div>

                      <div className="position-relative mb-24 ms-80 me-80">
                        <input
                          type="text"
                          placeholder="Search your orders here ..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="form-control"
                          style={{
                            maxWidth: "100%",
                            padding: "12px 16px",
                            borderRadius: "12px",
                          }}
                        />
                        <span className="position-absolute end-0 top-50 translate-middle-y me-16 py-12 ps-20 pe-12 border-start">
                          <i className="ph ph-magnifying-glass text-gray-500"></i>
                        </span>
                      </div>

                      {filteredOrders
                        .flatMap((order) =>
                          order.orderItems.map((item, itemIndex) => ({
                            item,
                            order,
                            key: `${order.id}-${itemIndex}`,
                          })),
                        )
                        .slice(0, visibleCount)
                        .map(({ item, order, key }) => {
                          const productReviews =
                            allReviewData?.filter(
                              (r) => r.productId === item.productId,
                            ) || [];

                          const finalAmount = item.amount || 0;
                          const unitPrice =
                            item.qty > 0 ? finalAmount / item.qty : finalAmount;

                          return (
                            <div
                              className="card cart-table border border-gray-100 rounded-16 mb-16 p-16 ms-80 me-80"
                              key={key}
                            >
                              <div className="row g-0">
                                {/* Product Image */}
                                <div
                                  className="col-12 col-md-3 d-flex align-items-center justify-content-center rounded-16 p-3 mb-3 mb-md-0"
                                  style={{
                                    backgroundColor: "#F8F8F8",
                                    width: "20%",
                                  }}
                                >
                                  <Link to={"/OrderDetails/" + item.id}>
                                    <ProductImage
                                      productId={item.productId}
                                      products={products}
                                      fetchProductDetails={fetchProductDetails}
                                      alt={item.productName}
                                    />
                                  </Link>
                                </div>

                                {/* Product Details */}
                                <div className="col-12 col-md-6 border border-gray-100 border-start-0 border-md-start-0 border-end-1 border-top-0 border-bottom-0 border-md-end-0 p-0 mb-3 mb-md-0">
                                  <div className="card-body d-flex flex-column h-100">
                                    <div className="flex-grow-1">
                                      <h6 className="card-title text-md fw-bold mb-2">
                                        <Link
                                          to={"/OrderDetails/" + item.id}
                                          className="link text-line-2"
                                        >
                                          {item.productName?.toUpperCase() ||
                                            ""}
                                        </Link>
                                      </h6>

                                      {/* Reviews */}
                                      {productReviews.length > 0 && (
                                        <div className="flex-align gap-8 mb-2">
                                          <span className="fw-medium text-neutral-600">
                                            <div className="badge bg-success">
                                              {ratingAvg1(productReviews)}{" "}
                                              <i className="ph-fill ph-star" />
                                            </div>
                                          </span>
                                          <span className="fw-medium text-gray-500">
                                            ({productReviews.length})
                                          </span>
                                        </div>
                                      )}
                                      <br />

                                      {/* Price */}
                                      <div className="d-flex hstack gap-8">
                                        <div className="text-lg fw-bold">
                                          ₹{finalAmount.toLocaleString("en-IN")}
                                        </div>
                                        {/* {item.qty > 1 && (
                                          <div className="text-gray-500 text-sm">
                                            (₹
                                            {unitPrice.toLocaleString("en-IN")}
                                            /unit)
                                          </div>
                                        )} */}
                                      </div>
                                      {/* <div className="text-gray-500 text-md fw-medium">
                                        {item.qty} {item.unit}
                                      </div> */}
                                      <div className="text-gray-500 text-md fw-medium">
                                        Inclusive of all taxes
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="col-12 col-md-3 d-flex flex-column align-items-end ps-16">
                                  <div>
                                    <h6 className="text-sm fw-semibold text-center text-capitalize">
                                      {item.status === "delivered" ? (
                                        <CheckCircle color="#064b11ff" />
                                      ) : (
                                        <CircleFadingArrowUp color="#064b11ff" />
                                      )}
                                      {`\u00A0\u00A0${item.status}`}
                                    </h6>
                                    <h6 className="text-sm fw-semibold text-center">
                                      {formatStatusDate(order, item.status)}
                                    </h6>
                                    <div className="mt-2">
                                      {item.status !== "delivered" &&
                                      item.status !== "cancelled" ? (
                                        <button
                                          className="btn btn-sm btn-main text-white flex-center mb-12"
                                          style={{
                                            width: "200px",
                                            border: "none",
                                          }}
                                          disabled={loading}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setSelectedOrder(order);
                                            setShowModal(true);
                                          }}
                                        >
                                          Track Order
                                        </button>
                                      ) : (
                                        <button
                                          className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white mb-12"
                                          style={{
                                            width: "200px",
                                            backgroundColor: "#ddd",
                                            border: "none",
                                          }}
                                          disabled={loading}
                                        >
                                          {item.status === "delivered"
                                            ? "Rate this product"
                                            : "Order Cancelled"}
                                        </button>
                                      )}
                                    </div>
                                    {item.status === "delivered" ? (
                                      <button
                                        className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                                        style={{
                                          width: "200px",
                                          backgroundColor: "#ddd",
                                          border: "none",
                                        }}
                                      >
                                        Return
                                      </button>
                                    ) : item.status !== "cancelled" ? (
                                      <button
                                        className="btn btn-sm text-neutral-600 flex-center hover-bg-main-600 hover-text-white"
                                        style={{
                                          width: "200px",
                                          backgroundColor: "#ddd",
                                          border: "none",
                                        }}
                                        disabled={
                                          loading ||
                                          item.status === "shipped" ||
                                          item.status === "cancel request"
                                        }
                                        onClick={(e) => {
                                          e.preventDefault();
                                          if (
                                            item.status !== "shipped" &&
                                            item.status !== "cancel request"
                                          ) {
                                            handleShowCancelModal(order);
                                          }
                                        }}
                                      >
                                        {item.status === "cancel request"
                                          ? "Cancellation Requested"
                                          : "Cancel Order"}
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      {/* Load More Button */}
                      {filteredOrders.flatMap((order) => order.orderItems)
                        .length > visibleCount && (
                        <div className="text-center my-32">
                          <button
                            className="btn btn-main text-white px-40 py-12 rounded-16 fw-bold"
                            onClick={handleLoadMore}
                          >
                            Load More
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {modalComponent && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 9999,
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      padding: "30px",
                      borderRadius: "16px",
                      width: "100%",
                      maxWidth: "500px",
                    }}
                  >
                    <div className="d-flex justify-content-end">
                      <h6 className="modal-header text-xl mb-16 ps-16 fw-bold w-100 d-flex">
                        Cancel Order{" "}
                      </h6>
                      <div onClick={() => setModalComponent(null)} className="">
                        <CircleX color="#ddd" />
                      </div>
                    </div>
                    {modalComponent}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Error mainText="No orders found!" />
          )}
        </>
      )}
    </div>
  );
};

const Cancel = ({ orderItemIds, getOrderItems, setShowModal }) => {
  const toast = useNotification();
  const [reason, setReason] = useState("");
  const [postLoading, setPostLoading] = useState(false);

  const cancelItems = async () => {
    const data = {
      userComment: String(reason).trim(),
      status: CANCEL_REQUEST,
    };

    setPostLoading(true);

    try {
      const cancelPromises = orderItemIds.map((id) =>
        api.put(
          `/orders/?id=${id}&flag=changestatus`,
          { ...data, orderItemId: id },
          customerReqConfig(),
        ),
      );

      const responses = await Promise.all(cancelPromises);

      responses.forEach((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: `Order item cancelled successfully`,
          });
        }
      });

      getOrderItems();
      setShowModal(false);
    } catch (error) {
      toast({
        type: ERROR,
        message: error.response?.data?.message || error.message,
      });
    } finally {
      setPostLoading(false);
    }
  };

  return (
    <div className="w-100">
      <textarea
        name="reason"
        id="reason"
        cols="30"
        rows="5"
        className="common-input form-control mb-3"
        placeholder="Please enter the reason for cancellation"
        style={{ resize: "none" }}
        onChange={(e) => setReason(e.target.value)}
        value={reason}
      />
      <button
        className={`mt-5 btn ${postLoading || !reason.trim() ? "btn-secondary" : "btn-main"}`}
        disabled={postLoading || !reason.trim()}
        onClick={(e) => preventClick(e, cancelItems)}
      >
        {postLoading ? "Cancelling..." : "Submit"}
      </button>
    </div>
  );
};

export default CustomerOrders;
